import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import * as React from 'react';
import classNames from '../../node_modules/classnames/index.js';
import styles from './Chip.module.scss.js';
import { applyCommonProps } from '../utility/commonProps.js';

var _excluded = ["children", "className", "leading", "trailing", "selected", "size"];
/**
 * Chips allow users to make selections, filter content, or trigger actions.
 *
 * - {@link https://livingdesign.walmart.com/components/chip/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/chip/react/ React documentation}
 *
 * ### Content
 *
 * #### Leading and trailing
 *
 * The Chip accepts leading and trailing icons through the optional `leading` and `trailing` props.
 *
 * ### Features
 *
 * #### Size
 *
 * The Chip comes in two sizes:
 *
 * - `small` (default)
 * - `large`
 *
 * Set the Chip's size using the `size` prop:
 *
 * ```tsx
 * <Chip size="small">Dorito</Chip>
 * <Chip size="large">Dorito</Chip>
 * ```
 *
 * #### States
 *
 * The Chip's states can be managed through the following optional props: `disabled` and `selected`.
 *
 * ```tsx
 * import * as React from "react";
 * import { Chip } from "@walmart-web/livingdesign-components";
 * import Icon from "@livingdesign/icons";
 *
 * export default () => {
 *   return (
 *     <>
 *       <Chip
 *         disabled
 *         leading={<Icon.Spark />}
 *         size="small"
 *       >
 *         Dorito
 *       </Chip>
 *       <Chip
 *         selected
 *         size="large"
 *         trailing={<Icon.Camera />}
 *       >
 *         Lays
 *       </Chip>
 *     </>
 *   );
 * }
 * ```
 */
var Chip = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _applyCommonProps = applyCommonProps(props, Chip),
    children = _applyCommonProps.children,
    className = _applyCommonProps.className,
    leading = _applyCommonProps.leading,
    trailing = _applyCommonProps.trailing,
    _applyCommonProps$sel = _applyCommonProps.selected,
    selected = _applyCommonProps$sel === void 0 ? false : _applyCommonProps$sel,
    _applyCommonProps$siz = _applyCommonProps.size,
    size = _applyCommonProps$siz === void 0 ? 'small' : _applyCommonProps$siz,
    rest = _objectWithoutProperties(_applyCommonProps, _excluded);
  return /*#__PURE__*/React.createElement("button", _extends({
    "aria-pressed": selected,
    className: classNames(styles.chip, size === 'large' && styles.large, size === 'small' && styles.small, selected && styles.selected, className),
    ref: ref,
    type: "button"
  }, rest), leading && /*#__PURE__*/React.createElement("span", {
    className: styles.leading
  }, leading), children, trailing && /*#__PURE__*/React.createElement("span", {
    className: styles.trailing
  }, trailing));
});
Chip.displayName = 'Chip';

export { Chip };
